<script setup lang="ts">
import { IconCheck } from '@tabler/icons-vue';
import { computed, ref } from 'vue';
import { toast } from 'vue3-toastify';

import IconCalendarCheck from '~/assets/icons/IconCalendarCheck.vue';
import IconCheckCircle from '~/assets/icons/IconCheckCircle.vue';
import IconFileText from '~/assets/icons/IconFileText.vue';
import StatusCard from '~/components/shared/StatusCard.vue';
import useGroup from '~/hooks/useGroup';
import usePermission from '~/hooks/usePermission';
import { FinancingFlowApi } from '~/services/financing-flow-api/financing-flow.types';
import installationRiskApi, { InstallationApprovalError } from '~/services/installation-risk-api/installation-risk-api';

import ApproveInstallationModal, { type ApprovalData } from '../../components/ApproveInstallationModal.vue';
import ChangeInverterSerialNumberModal from '../../components/ChangeInverterSerialNumberModal.vue';
import ReasonModal from '../../components/ReasonModal.vue';
import { COMMISSIONING_RESET_GROUP } from '../../constants';
import useInvertersInfo from '../../queries/useInvertersInfo';
import useResetCommissioning from '../../queries/useResetCommissioning';

interface Emits {
  (_e: 'refresh'): void;
}

interface Props {
  financing: FinancingFlowApi.FinancingV2;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const completedAt = computed(() => props.financing.section_statuses.installation?.completed_at || undefined);
const submittedAt = computed(() => props.financing.section_statuses.installation?.submitted_at || undefined);

const { hasAccess: canApproveInstallation } = usePermission(['installation_approvers:edit']);

const isCurrentStepInstallation = computed(() => props.financing.step === 'installation');

const isFinancingStatusValid = computed(() => {
  const status = props.financing.status?.toLowerCase().trim();
  if (!status) return false;
  const invalidStatuses = ['canceled', 'reproved', 'expired', 'approved'];
  return !invalidStatuses.includes(status);
});

const showModal = ref(false);
const isApproving = ref(false);
const modalErrorMessage = ref('');

const handleApproveInstallation = () => {
  modalErrorMessage.value = '';
  showModal.value = true;
};

const handleModalApprove = async (data: ApprovalData) => {
  isApproving.value = true;
  modalErrorMessage.value = '';

  try {
    const projectId = props.financing.project_id;

    const apiData = {
      customerEvidenceFiles: data.customerEvidenceFiles,
      hasPhoneChange: data.hasPhoneChange,
      newPhoneNumber: data.newPhoneNumber,
      notes: data.notes,
      zendeskTicket: data.zendeskTicket || undefined,
    };

    await installationRiskApi.bypassCustomerConfirmation(projectId, apiData);

    showModal.value = false;

    toast.success('Instalação aprovada manualmente com sucesso!');

    emit('refresh');
  } catch (error: unknown) {
    if (error instanceof InstallationApprovalError) {
      modalErrorMessage.value = error.message;
    } else {
      modalErrorMessage.value = 'Erro inesperado ao aprovar instalação. Por favor, tente novamente.';
    }
    toast.error('Erro ao aprovar instalação');
  } finally {
    isApproving.value = false;
  }
};

// Project ID para buscar inversores
const projectId = computed(() => props.financing.project_id);

// Carregar dados dos inversores quando a página abrir
const { data: invertersData, error: invertersError, isLoading: isLoadingInverters } = useInvertersInfo(projectId);
const inverters = computed(() => invertersData.value?.data || []);

// Elegibilidade baseada no status dos inversores
// isInstallationCompleted: se algum inversor estiver com installation_status "APPROVED"
const isInstallationCompleted = computed(() => inverters.value.some(
  (inverter) => inverter.installation_status === 'APPROVED',
));

// isWaitingCustomerConfirmation: se algum inversor estiver com installation_status "WAITING_CUSTOMER_CONFIRMATION"
const isWaitingCustomerConfirmation = computed(() => inverters.value.some(
  (inverter) => inverter.installation_status === 'WAITING_CUSTOMER_CONFIRMATION',
));

const shouldShowButton = computed(() => {
  if (!canApproveInstallation.value) return false;

  if (!isFinancingStatusValid.value) return false;

  if (completedAt.value) return false;

  if (!isCurrentStepInstallation.value) return false;

  if (!isWaitingCustomerConfirmation.value) return false;

  return true;
});

const showChangeSerialNumberButton = computed(
  () => isInstallationCompleted.value || isWaitingCustomerConfirmation.value,
);

const showChangeSerialNumberModal = ref(false);

const openChangeSerialNumberModal = () => {
  showChangeSerialNumberModal.value = true;
};

const { hasAccess: canResetCommissioning } = useGroup([COMMISSIONING_RESET_GROUP]);
const resetCommissioningMutation = useResetCommissioning();
const showResetModal = ref(false);

const openResetModal = () => {
  showResetModal.value = true;
};

const handleResetConfirm = async (data: { reason: string }) => {
  try {
    await resetCommissioningMutation.mutateAsync({
      projectId: projectId.value,
      reason: data.reason,
    });
    showResetModal.value = false;
  } catch {
    // Error handled by mutation
  }
};
</script>

<template>
  <div class="installation-step">
    <div class="mb-4">
      <div class="row g-3">
        <div class="status-card-col">
          <StatusCard
            title="Submetido em"
            :datetime="submittedAt"
            :icon="IconFileText"
            type="informative"
          />
        </div>

        <div class="status-card-col">
          <StatusCard
            title="Concluído em"
            :datetime="completedAt"
            :icon="IconCheckCircle"
            type="positive"
          />
        </div>

        <div class="status-card-col">
          <StatusCard
            title="Data de Instalação"
            :datetime="financing.installed_at || undefined"
            :icon="IconCalendarCheck"
            :type="financing.installed_at ? 'positive' : 'default'"
          />
        </div>
      </div>
    </div>

    <div v-if="shouldShowButton" class="mt-4">
      <div class="alert alert-warning">
        <div class="alert-with-icon">
          <div class="content">
            <h5 class="heading">Aprovação Manual de Instalação</h5>
            <p class="text mb-3">
              Use este botão apenas quando a confirmação automática via WhatsApp falhar.
              Será necessário anexar evidências da instalação.
            </p>
            <button
              class="btn btn-success"
              type="button"
              :disabled="isApproving"
              @click="handleApproveInstallation"
            >
              <IconCheck :size="20" class="me-2" />
              OK do Cliente
            </button>
          </div>
        </div>
      </div>
    </div>

    <ApproveInstallationModal
      v-model="showModal"
      :loading="isApproving"
      :error-message="modalErrorMessage"
      @approve="handleModalApprove"
    />

    <div v-permission="['tech_n1_support:edit']">
      <div v-if="showChangeSerialNumberButton" class="mt-4">
        <button
          class="btn btn-primary"
          type="button"
          @click="openChangeSerialNumberModal"
        >
          <i class="ti ti-exchange" />
          Troca de SN de Inversores
        </button>
      </div>

      <ChangeInverterSerialNumberModal
        v-model="showChangeSerialNumberModal"
        :financing-id="financing.id"
        :inverters="inverters"
        :is-loading-inverters="isLoadingInverters"
        :inverters-error="invertersError"
        :partner-id="financing.partner_id || 0"
        :project-id="projectId"
        @success="() => {}"
      />
    </div>

    <div v-if="canResetCommissioning" class="mt-4">
      <div class="alert alert-danger">
        <div class="alert-with-icon">
          <div class="content">
            <h5 class="heading">Resetar Comissionamento</h5>
            <p class="text mb-3">
              Remove os dados de comissionamento e os inversores instalados desta instalação,
              liberando o parceiro para re-submeter os dados na tela do integrador. Ação irreversível.
            </p>
            <button
              class="btn btn-danger"
              type="button"
              :disabled="resetCommissioningMutation.isPending.value"
              @click="openResetModal"
            >
              <i class="ti ti-refresh-alert me-2" />
              Resetar Comissionamento
            </button>
          </div>
        </div>
      </div>
    </div>

    <ReasonModal
      v-model="showResetModal"
      title="Resetar Comissionamento"
      field-label="Motivo do reset"
      placeholder="Descreva o motivo do reset (ex.: número do chamado, causa do travamento)"
      primary-button-text="Confirmar reset"
      :min-length="10"
      :max-length="500"
      :is-loading="resetCommissioningMutation.isPending.value"
      @confirm="handleResetConfirm"
    />
  </div>
</template>
<style scoped lang="scss">
.installation-step {
  .status-card-col {
    @media (min-width: 768px) {
      flex: 0 0 auto;
      width: 33.33333333%;
    }
  }

  .badge {
    font-size: 0.75rem;
  }
}
</style>
